import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  Stethoscope, Building2, Search, Plus, QrCode, Calendar, 
  Users, Activity, Syringe, TestTube, Clock, ChevronRight,
  LogOut, Settings, Bell, Dog, Cat, PawPrint, CheckCircle,
  AlertCircle, FileText, TrendingUp
} from "lucide-react";
import VetKBValidation from "@/components/VetKBValidation";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

export default function VetDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [vet, setVet] = useState(null);
  const [stats, setStats] = useState(null);
  const [patients, setPatients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [shareToken, setShareToken] = useState("");
  const [addingPatient, setAddingPatient] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('vet_token');
    if (!token) {
      navigate('/vet/login');
      return;
    }

    try {
      const res = await fetch(`${API}/api/vet/me`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        setVet(data);
        fetchStats(token);
        fetchPatients(token);
      } else {
        localStorage.removeItem('vet_token');
        navigate('/vet/login');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      navigate('/vet/login');
    }
  };

  const fetchStats = async (token) => {
    try {
      const res = await fetch(`${API}/api/vet/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchPatients = async (token) => {
    try {
      const res = await fetch(`${API}/api/vet/patients`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setPatients(data);
      }
    } catch (error) {
      console.error('Error fetching patients:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('vet_token');
    try {
      await fetch(`${API}/api/vet/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
    } catch (error) {
      // Ignore
    }
    localStorage.removeItem('vet_token');
    localStorage.removeItem('vet_profile');
    navigate('/vet/login');
  };

  const handleAcceptShare = async () => {
    if (!shareToken.trim()) {
      toast.error("Inserisci il codice di condivisione");
      return;
    }

    setAddingPatient(true);
    const token = localStorage.getItem('vet_token');

    try {
      const res = await fetch(`${API}/api/vet/patients/accept-share?share_token=${shareToken.trim()}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });

      const data = await res.json();

      if (res.ok) {
        toast.success(data.message || "Paziente aggiunto!");
        setShowQRScanner(false);
        setShareToken("");
        fetchPatients(token);
        fetchStats(token);
      } else {
        toast.error(data.detail || "Errore");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setAddingPatient(false);
    }
  };

  const filteredPatients = patients.filter(p => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      p.pet_name?.toLowerCase().includes(q) ||
      p.owner_name?.toLowerCase().includes(q) ||
      p.microchip?.includes(q)
    );
  });

  const speciesIcons = {
    dog: Dog,
    cat: Cat,
    default: PawPrint
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-[#2E7D32] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[#86868B]">Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F7]">
      {/* Header */}
      <header className="bg-white border-b border-black/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2E7D32] to-[#4CAF50] flex items-center justify-center">
                {vet?.account_type === "clinica" ? (
                  <Building2 className="text-white" size={20} />
                ) : (
                  <Stethoscope className="text-white" size={20} />
                )}
              </div>
              <div>
                <p className="font-bold text-[#1D1D1F]">{vet?.name}</p>
                <p className="text-xs text-[#86868B]">
                  {vet?.account_type === "clinica" ? "Clinica Veterinaria" : "Veterinario"}
                  {vet?.verified && (
                    <span className="inline-flex items-center gap-1 ml-2 text-[#2E7D32]">
                      <CheckCircle size={12} /> Verificato
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" className="rounded-xl">
                <Bell size={20} />
              </Button>
              <Button variant="ghost" size="icon" className="rounded-xl">
                <Settings size={20} />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="rounded-xl text-red-500"
                onClick={handleLogout}
              >
                <LogOut size={20} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Verification Warning */}
        {vet && !vet.verified && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-amber-800">Account in attesa di verifica</p>
              <p className="text-sm text-amber-700 mt-1">
                Il tuo account è in fase di verifica. Potrai accedere a tutte le funzionalità dopo l'approvazione (24-48 ore).
              </p>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard 
            icon={Users} 
            label="Pazienti totali" 
            value={stats?.total_patients || 0}
            color="bg-blue-100 text-blue-600"
          />
          <StatCard 
            icon={Calendar} 
            label="Appuntamenti oggi" 
            value={stats?.appointments_today || 0}
            color="bg-green-100 text-green-600"
          />
          <StatCard 
            icon={Syringe} 
            label="Vaccini somministrati" 
            value={stats?.vaccines_administered || 0}
            color="bg-purple-100 text-purple-600"
          />
          <StatCard 
            icon={TestTube} 
            label="Esami effettuati" 
            value={stats?.exams_performed || 0}
            color="bg-orange-100 text-orange-600"
          />
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Patients List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-[#1D1D1F]">I miei pazienti</h2>
                <Button 
                  onClick={() => setShowQRScanner(true)}
                  className="bg-[#2E7D32] hover:bg-[#1B5E20] rounded-xl"
                  data-testid="add-patient-btn"
                >
                  <QrCode size={18} className="mr-2" /> Aggiungi paziente
                </Button>
              </div>

              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#86868B]" size={18} />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Cerca per nome, proprietario o microchip..."
                  className="h-12 pl-12 rounded-xl bg-[#F5F5F7] border-0"
                  data-testid="search-patients"
                />
              </div>

              {/* Patient List */}
              {filteredPatients.length === 0 ? (
                <div className="text-center py-12">
                  <PawPrint className="mx-auto text-[#D1D1D6] mb-4" size={48} />
                  <h3 className="font-semibold text-[#1D1D1F] mb-2">Nessun paziente</h3>
                  <p className="text-[#86868B] mb-4">
                    Aggiungi pazienti tramite QR code o codice di condivisione
                  </p>
                  <Button 
                    onClick={() => setShowQRScanner(true)}
                    variant="outline"
                    className="rounded-xl"
                  >
                    <Plus size={18} className="mr-2" /> Aggiungi paziente
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredPatients.map(patient => {
                    const SpeciesIcon = speciesIcons[patient.species] || speciesIcons.default;
                    return (
                      <Link
                        key={patient.pet_id}
                        to={`/vet/patient/${patient.pet_id}`}
                        className="flex items-center gap-4 p-4 bg-[#F5F5F7] rounded-2xl hover:bg-[#EBEBED] transition-colors group"
                        data-testid={`patient-${patient.pet_id}`}
                      >
                        {/* Pet Photo */}
                        <div className="w-14 h-14 rounded-xl bg-white flex items-center justify-center overflow-hidden">
                          {patient.photo_url ? (
                            <img 
                              src={`${API.replace('/api', '')}${patient.photo_url}`}
                              alt={patient.pet_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <SpeciesIcon className="text-[#86868B]" size={24} />
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-[#1D1D1F] truncate">{patient.pet_name}</p>
                            {patient.source === "shared" && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                                Condiviso
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-[#86868B] truncate">
                            {patient.owner_name} • {patient.breed || patient.species}
                          </p>
                          {patient.microchip && (
                            <p className="text-xs font-mono text-[#86868B] mt-0.5">
                              {patient.microchip}
                            </p>
                          )}
                        </div>

                        {/* Stats */}
                        <div className="text-right hidden sm:block">
                          <p className="text-sm font-medium text-[#1D1D1F]">
                            {patient.total_visits} visite
                          </p>
                          {patient.last_visit && (
                            <p className="text-xs text-[#86868B]">
                              Ultima: {new Date(patient.last_visit).toLocaleDateString('it-IT')}
                            </p>
                          )}
                        </div>

                        <ChevronRight className="text-[#86868B] group-hover:text-[#1D1D1F] transition-colors" size={20} />
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Upcoming Appointments */}
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-[#1D1D1F]">Prossimi appuntamenti</h3>
                <Link to="/vet/calendar" className="text-sm text-[#2E7D32] hover:underline">
                  Vedi tutti
                </Link>
              </div>

              {stats?.upcoming_appointments?.length > 0 ? (
                <div className="space-y-3">
                  {stats.upcoming_appointments.slice(0, 5).map((apt, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-[#F5F5F7] rounded-xl">
                      <div className="w-10 h-10 rounded-lg bg-[#2E7D32]/10 flex items-center justify-center">
                        <Clock className="text-[#2E7D32]" size={18} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#1D1D1F] text-sm truncate">{apt.pet_name}</p>
                        <p className="text-xs text-[#86868B]">
                          {apt.date} alle {apt.time} • {apt.service_type}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#86868B] text-sm text-center py-6">
                  Nessun appuntamento programmato
                </p>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-3xl p-6 shadow-sm">
              <h3 className="font-bold text-[#1D1D1F] mb-4">Azioni rapide</h3>
              <div className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start rounded-xl h-12"
                  onClick={() => setShowQRScanner(true)}
                >
                  <QrCode size={18} className="mr-3" /> Scansiona QR paziente
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start rounded-xl h-12"
                  asChild
                >
                  <Link to="/vet/calendar">
                    <Calendar size={18} className="mr-3" /> Calendario appuntamenti
                  </Link>
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start rounded-xl h-12"
                  asChild
                >
                  <Link to="/vet/stats">
                    <TrendingUp size={18} className="mr-3" /> Statistiche
                  </Link>
                </Button>
              </div>
            </div>

            {/* Species Distribution */}
            {stats?.patients_by_species && Object.keys(stats.patients_by_species).length > 0 && (
              <div className="bg-white rounded-3xl p-6 shadow-sm">
                <h3 className="font-bold text-[#1D1D1F] mb-4">Pazienti per specie</h3>
                <div className="space-y-3">
                  {Object.entries(stats.patients_by_species).map(([species, count]) => {
                    const SpeciesIcon = speciesIcons[species] || speciesIcons.default;
                    const labels = { dog: 'Cani', cat: 'Gatti', horse: 'Cavalli', bird: 'Uccelli', rabbit: 'Conigli' };
                    return (
                      <div key={species} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <SpeciesIcon size={18} className="text-[#86868B]" />
                          <span className="text-sm text-[#1D1D1F]">{labels[species] || species}</span>
                        </div>
                        <span className="font-semibold text-[#1D1D1F]">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* KB Validation Component - Now with unified auth */}
            <VetKBValidation vetToken={localStorage.getItem('vet_token')} />
          </div>
        </div>
      </main>

      {/* Add Patient Dialog */}
      <Dialog open={showQRScanner} onOpenChange={setShowQRScanner}>
        <DialogContent className="max-w-md rounded-3xl p-0 border-0">
          <div className="p-6">
            <DialogHeader className="mb-6">
              <DialogTitle className="text-xl font-bold text-[#1D1D1F] flex items-center gap-3">
                <QrCode className="text-[#2E7D32]" size={24} />
                Aggiungi paziente
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-6">
              {/* QR Scanner placeholder */}
              <div className="aspect-square bg-[#F5F5F7] rounded-2xl flex items-center justify-center">
                <div className="text-center p-6">
                  <QrCode className="mx-auto text-[#86868B] mb-4" size={64} />
                  <p className="text-[#86868B] mb-4">
                    Fai scansionare il QR code dal proprietario dell'animale
                  </p>
                  <p className="text-sm text-[#86868B]">
                    Oppure inserisci il codice manualmente:
                  </p>
                </div>
              </div>

              {/* Manual code input */}
              <div>
                <Input
                  value={shareToken}
                  onChange={(e) => setShareToken(e.target.value)}
                  placeholder="Codice condivisione (es. share_abc123...)"
                  className="h-12 rounded-xl font-mono text-sm"
                  data-testid="share-token-input"
                />
              </div>

              <Button 
                onClick={handleAcceptShare}
                disabled={addingPatient || !shareToken.trim()}
                className="w-full h-12 bg-[#2E7D32] hover:bg-[#1B5E20] rounded-xl"
                data-testid="accept-share-btn"
              >
                {addingPatient ? "Aggiunta in corso..." : "Aggiungi paziente"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Stat Card Component
function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm">
      <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center mb-3`}>
        <Icon size={20} />
      </div>
      <p className="text-2xl font-bold text-[#1D1D1F]">{value}</p>
      <p className="text-sm text-[#86868B]">{label}</p>
    </div>
  );
}
